import { useState } from 'react';
import { useSnapshot } from 'valtio';

import Modal from '@cartwhl/ui/molecules/Modal';

import useFfmpeg, { ProgressInfo, VideoEditSettings } from 'hooks/useFfmpeg';
import {
  animationGenerationStore,
  navigationStore,
  projectStore,
} from 'stores';

import { ModalConfig } from '../constants';
import { usePreparedVideo, useVideoCache } from '../hooks';
import { useThumbnails } from '../hooks';
import { EditableVideo } from '../types';
import { getVideoEditCacheKey, omitKeys } from '../utils';
import Cropping from './Cropping';
import FileUpload from './FileUpload';
import MediaCapture from './MediaCapture';
import Trimming from './Trimming';
import VideoFilePicker from './VideoFilePicker';

export type ModalStatus =
  | 'file picker'
  | 'media capture'
  | 'trimming start'
  | 'trimming end'
  | 'cropping'
  | 'upload';

export default function VideoUploadModal({
  close,
  initialStatus,
  initialSourceVideoBlob,
}: {
  close: () => void;
  initialStatus: ModalStatus;
  initialSourceVideoBlob?: File | null; // Used when file is uploaded before the modal is opened
}) {
  const { existingMediaID, jobData } = useSnapshot(
    animationGenerationStore.state,
  );
  const { currentProject } = useSnapshot(projectStore.state);
  const [status, setStatus] = useState<ModalStatus>(initialStatus);
  const initialVideoProgress: ProgressInfo = {
    percentage: 0,
    time: '00:00:00',
    message: 'Getting your video ready…',
  };
  const [progress, setProgress] = useState<ProgressInfo | null>(
    initialSourceVideoBlob ? initialVideoProgress : null,
  );
  const [saveOriginal, setSaveOriginal] = useState(true);

  // Cache of videos that have been edited. This is used to avoid re-editing the same video.
  const { addToCache, getFromCache } = useVideoCache();

  // Contains the edits the user has made to the video
  const [editSettings, setEditSettings] = useState<VideoEditSettings>();

  // Source video is the video that the user uploads or captures
  const [sourceVideoBlob, setSourceVideoBlob] = useState<File | null>(
    initialSourceVideoBlob ?? null,
  );
  // Prepared video is the video that has been optimized for custom playback
  const { preparedVideo, sourceMetadata } = usePreparedVideo(
    sourceVideoBlob,
    (video) => {
      // We use the prepared video as initial input to the cropping and trimming UI.
      setCroppingInputVideo(video);
      setTrimmingInputVideo(video);
      addToCache(getVideoEditCacheKey(editSettings), video);
    },
    setProgress,
  );
  // Cropping input video is the video that is used for the cropping UI. It should not include any cropping and rotation.
  const [croppingInputVideo, setCroppingInputVideo] =
    useState<EditableVideo | null>(null);
  // Trimming input video is the video that is used for the trimming UI. It should not include any trimming.
  const [trimmingInputVideo, setTrimmingInputVideo] =
    useState<EditableVideo | null>(null);
  const trimmingThumbnails = useThumbnails(trimmingInputVideo);

  const { getVideoMetadata, editVideo } = useFfmpeg();

  /*
    Future support for browsing saved media:
    All we need to do is fetch the media they browse to ensure that we have the correct
    mediaDownloadURL set, and then initiate the videoUploadModal at the file picker
    await animationGenerationStore.fetchExistingMedia(
      'media-01JEQG07XDFQ883AMV54HWYNWK',
    );
    navigationStore.setVideoUploadModal('file picker');
  */

  const handleSubmit = async (
    editSettings: VideoEditSettings,
    clearJobData = true,
  ) => {
    setEditSettings(editSettings);

    if (!sourceVideoBlob) {
      console.error('No source video blob');
      return;
    }

    setProgress({
      percentage: 0,
      time: '00:00:00',
      message: 'Uploading…',
    });

    const sanitizedEdits = sanitizeVideoEditSettings(editSettings);
    const hasEdits = !!(
      sanitizedEdits.trim ||
      sanitizedEdits.crop ||
      sanitizedEdits.mirror ||
      sanitizedEdits.flip
    );

    const originalFilenameNoExtension = sourceVideoBlob.name
      .split('.')
      .slice(0, -1)
      .join('.');
    const originalExtension =
      sourceVideoBlob.name.split('.').pop()?.toLowerCase() || 'mp4';

    let outputBlob: Blob;
    let outputExtension: string;

    if (hasEdits) {
      // Apply edits directly to the original source at high quality (CRF 18,
      // native FPS). Bypasses the low-bitrate prepared video entirely.
      const inputFileName = `source.${originalExtension}`;
      const outputFileName = `edited_hq.mp4`;

      outputBlob = await editVideo({
        inputBlob: sourceVideoBlob,
        inputFileName,
        outputFileName,
        edits: sanitizedEdits,
        onProgress: setProgress,
        progressMessage: 'Processing video…',
        preserveQuality: true,
      });
      outputExtension = 'mp4';
    } else {
      // No edits needed, send the original file at full quality.
      outputBlob = sourceVideoBlob;
      outputExtension = originalExtension;
    }

    const filename = `${originalFilenameNoExtension}.${outputExtension}`;
    const mimeType =
      outputExtension === 'mp4' ? 'video/mp4' : `video/${outputExtension}`;
    const outputFile = new File([outputBlob], filename, { type: mimeType });

    let mediaID = existingMediaID;
    if (saveOriginal && sourceMetadata) {
      const sourceFile = new File([sourceVideoBlob], sourceVideoBlob.name, {
        type: sourceVideoBlob.type || `video/${originalExtension}`,
      });
      mediaID = await animationGenerationStore.saveMediaToProfile(sourceFile, {
        duration: sourceMetadata.duration,
        extension: originalExtension,
        mediaName: sourceVideoBlob.name,
        resolution: `${sourceMetadata.width}x${sourceMetadata.height}`,
      });
    }

    setProgress({
      percentage: 100,
      time: '00:00:00',
      message: 'Uploading…',
    });

    if (currentProject.data) {
      projectStore.beginMediaUpload(outputFile, 'video', mediaID);
      navigationStore.closeModal();
    } else {
      if (clearJobData) {
        animationGenerationStore.resetJobData();
      }
      animationGenerationStore.beginVideoUpload(outputFile, mediaID);
    }

    setStatus('upload');
  };

  const sanitizeVideoEditSettings = (edits: VideoEditSettings) => {
    // If trim values are [0, duration] then we remove them, to prevent unnecessary encoding
    if (
      edits.trim &&
      edits.trim[0] === 0 &&
      edits.trim[1] === preparedVideo?.metadata?.duration
    ) {
      return {
        ...edits,
        trim: undefined,
      };
    }

    return edits;
  };

  const getEditedVideo = async (
    edits: VideoEditSettings,
    options?: {
      progressMessage?: string;
      clearProgressOnDone?: boolean;
    },
  ) => {
    const { progressMessage, clearProgressOnDone = true } = options ?? {};

    // Fist check the cache
    const sanitizedEdits = sanitizeVideoEditSettings(edits);
    const cacheKey = getVideoEditCacheKey(sanitizedEdits);
    const cachedVideo = getFromCache(cacheKey);
    if (cachedVideo) {
      if (clearProgressOnDone) {
        setProgress(null);
      }
      return cachedVideo;
    }

    // If the video is not in the cache, we need to edit it
    const inputVideo = preparedVideo;
    if (!inputVideo) return;

    setProgress({
      percentage: 0,
      time: '00:00:00',
      message: progressMessage ?? 'Editing video…',
    });

    const outputFileName = `edited${cacheKey}.mp4`;

    const editedBlob = await editVideo({
      inputBlob: inputVideo.blob,
      inputFileName: inputVideo.filename,
      outputFileName,
      edits: sanitizedEdits,
      onProgress: setProgress,
      progressMessage,
    });

    const metadata = await getVideoMetadata(editedBlob);

    const video = {
      blob: editedBlob,
      filename: outputFileName,
      mimeType: editedBlob.type,
      metadata: metadata,
    };

    addToCache(cacheKey, video);

    // Clean up
    if (clearProgressOnDone) {
      setProgress(null);
    }

    return video;
  };

  return (
    <Modal
      open={true}
      title={ModalConfig[status].title}
      maxWidth={ModalConfig[status].maxWidth}
      verticalBleed={false}
      closeOnClickOutside={false}
      fullscreenOnMobile
    >
      {status === 'file picker' && (
        <VideoFilePicker
          close={close}
          next={async (file) => {
            setProgress(initialVideoProgress);
            setSourceVideoBlob(file);
            setStatus('trimming start');
          }}
        />
      )}
      {status === 'media capture' && (
        <MediaCapture
          close={close}
          next={(file) => {
            setProgress(initialVideoProgress);
            setSourceVideoBlob(file);
            setStatus('trimming start');
          }}
        />
      )}

      {(status === 'trimming start' || status === 'trimming end') && (
        <Trimming
          defaultEditSettings={editSettings}
          inputVideo={trimmingInputVideo}
          mobileTrimbarMode={status === 'trimming start' ? 'start' : 'end'}
          intermediateNext={() => {
            if (status === 'trimming start') {
              setStatus('trimming end');
            } else {
              setStatus('cropping');
            }
          }}
          intermediateBack={() => setStatus('trimming start')}
          hasExistingJobData={!!jobData}
          close={close}
          openCropping={async (trim) => {
            setEditSettings({ ...editSettings, trim });
            const trimmedVideo = await getEditedVideo(
              { trim, flip: editSettings?.flip, mirror: editSettings?.mirror },
              { progressMessage: 'Trimming video…' },
            );
            if (trimmedVideo) {
              setCroppingInputVideo(trimmedVideo);
            }
            setStatus('cropping');
          }}
          next={(trim, clearJobData) => {
            handleSubmit({ ...editSettings, trim }, Boolean(clearJobData));
          }}
          progress={progress}
          thumbnails={trimmingThumbnails}
          saveOriginal={saveOriginal}
          setSaveOriginal={setSaveOriginal}
        />
      )}

      {status === 'cropping' && croppingInputVideo && (
        <Cropping
          defaultEditValues={omitKeys(editSettings ?? {}, ['trim'])}
          close={close}
          back={() => setStatus('trimming end')}
          inputVideo={croppingInputVideo}
          hasExistingJobData={!!jobData}
          onMirrorOrFlipChange={async (edits) => {
            const update = {
              ...editSettings,
              ...edits,
            };
            setEditSettings(update);
            const editedVideo = await getEditedVideo(
              { ...update, crop: undefined }, // don't apply cropping inside cropping mode
              { progressMessage: 'Flipping the video…' },
            );

            if (editedVideo) {
              setCroppingInputVideo(editedVideo);
            }
          }}
          openTrimming={async (edits) => {
            setEditSettings((prev) => ({
              ...prev,
              ...edits,
            }));
            const croppedVideo = await getEditedVideo(edits, {
              progressMessage: 'Cropping the video…',
            });
            if (croppedVideo) {
              setTrimmingInputVideo(croppedVideo);
            }
            setStatus('trimming start');
          }}
          next={(edits, clearJobData) => {
            handleSubmit({ ...editSettings, ...edits }, clearJobData);
          }}
          progress={progress}
        />
      )}

      {status === 'upload' && <FileUpload close={close} />}
    </Modal>
  );
}
